package recommendations

/**
 * Single source of truth for the dashboard "Savings Recommendation" card.
 *
 * Each platform (web/iOS/Android) computed this state inline, and the
 * implementations drifted: one web renderer suppressed the "Available to save"
 * line when the month had no income yet, while another still printed
 * "You're over budget by $X" — the autopay-before-payday false alarm. Every
 * renderer must call [computeSavingsRecommendationCardState] and drive its
 * layout off the returned state (and [showsAvailableAmount]) so the decision
 * can never diverge again.
 *
 * ## Which month the card is about
 *
 * The card is scoped to the dashboard's **display month** — the last complete month
 * by default, or whichever month the user stepped to — like every other month-scoped
 * widget. It is not scoped to the current calendar month. On the 3rd of August, "you
 * have $8 available to save" is two days of data reported as if it were a month, and
 * it sat next to a dashboard showing July. Callers pass the display month's totals
 * and say whether that month [monthInProgress] is still running; the state machine
 * uses that to tell "the month has barely started" apart from "this finished month
 * has no transactions in it at all".
 */
sealed class SavingsRecommendationCardState {
    /** No goals configured — invite the user to create one. */
    object NoGoals : SavingsRecommendationCardState()

    /**
     * Has goals, month just started, no transactions yet. Show "available"
     * (which will read $0) plus a hint that explains nothing has landed.
     *
     * Only reachable while the displayed month is the current, still-running one.
     */
    data class EarlyMonth(val availableAmount: Double) : SavingsRecommendationCardState()

    /**
     * Has goals, and the displayed month is over and holds no transactions at all —
     * a month before the accounts were connected, or one the user stepped back to.
     * The early-month hint would claim the month "is just getting started", which is
     * false, so this state gets its own line.
     */
    object NoMonthData : SavingsRecommendationCardState()

    /**
     * Has goals, expenses landed, income hasn't arrived yet. The "available"
     * figure will be misleadingly negative — suppress it and show only the
     * explanation so we don't scold the user for autopay timing.
     */
    object ExpensesBeforeIncome : SavingsRecommendationCardState()

    /**
     * Has goals + room to save: show the allocation breakdown. The breakdown
     * itself is rendered per-platform (each computes its own goal split); this
     * state only signals that there IS one to show.
     */
    data class HasAllocations(
        val availableAmount: Double,
    ) : SavingsRecommendationCardState()

    /**
     * Income has arrived, savings ≤ 0: genuine overspend. Show "available",
     * a nudge to spend less, and the Review Budget CTA.
     */
    data class SpendLessNudge(val availableAmount: Double) : SavingsRecommendationCardState()

    /** Whether the platform should render the "Available to save: $X" line. */
    val showsAvailableAmount: Boolean
        get() = when (this) {
            NoGoals, ExpensesBeforeIncome, NoMonthData -> false
            is EarlyMonth, is HasAllocations, is SpendLessNudge -> true
        }
}

/**
 * Decide which card state to render. All inputs are derived per-platform from
 * the same data, so the same inputs must produce the same state on every
 * platform.
 *
 * @param hasGoals        user has at least one savings goal configured
 * @param hasMonthData    any transactions have landed in the displayed month
 * @param hasIncomeYet    confirmed income for the displayed month is > 0
 * @param actualSavings   displayed month's income minus expenses (the "available" figure)
 * @param hasAllocations  there is at least one goal allocation to show (each
 *                        platform computes its own split; this is just whether
 *                        the breakdown is non-empty)
 * @param monthInProgress the displayed month is the current, still-running calendar
 *                        month. Only then can an empty month mean "it just started".
 */
fun computeSavingsRecommendationCardState(
    hasGoals: Boolean,
    hasMonthData: Boolean,
    hasIncomeYet: Boolean,
    actualSavings: Double,
    hasAllocations: Boolean,
    monthInProgress: Boolean,
): SavingsRecommendationCardState {
    if (!hasGoals) return SavingsRecommendationCardState.NoGoals

    if (hasAllocations) {
        return SavingsRecommendationCardState.HasAllocations(actualSavings)
    }

    if (actualSavings > 0.0) {
        // Edge case: positive savings but optimizer produced no allocations.
        // Fall through to the spend-less nudge path so the user still gets a
        // useful next-step rather than an empty card.
        return SavingsRecommendationCardState.SpendLessNudge(actualSavings)
    }

    return when {
        !hasMonthData && monthInProgress -> SavingsRecommendationCardState.EarlyMonth(actualSavings)
        !hasMonthData -> SavingsRecommendationCardState.NoMonthData
        !hasIncomeYet -> SavingsRecommendationCardState.ExpensesBeforeIncome
        else -> SavingsRecommendationCardState.SpendLessNudge(actualSavings)
    }
}
